"use client";

import { useEffect, useRef } from "react";
import {
  hasConsent,
  CONSENT_CHANGED_EVENT,
  type ConsentPreferences,
} from "@/lib/legal/consent";

const META_PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID;

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  push?: FbqFn;
};

function loadMetaPixel(pixelId: string): void {
  if (typeof window === "undefined") return;

  const w = window as Window & { fbq?: FbqFn; _fbq?: FbqFn };

  if (w.fbq) return; // already loaded

  const n: FbqFn = function (...args: unknown[]) {
    if (n.callMethod) {
      n.callMethod(...args);
    } else {
      n.queue?.push(args);
    }
  };
  if (!w._fbq) w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  w.fbq = n;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode)
    firstScript.parentNode.insertBefore(script, firstScript);
  else document.head.appendChild(script);

  w.fbq("init", pixelId);
  w.fbq("track", "PageView");
}

export default function ConditionalMetaPixel() {
  const loaded = useRef(false);

  useEffect(() => {
    if (!META_PIXEL_ID) return;

    // Load immediately if consent is already granted.
    if (hasConsent("analytics") && !loaded.current) {
      loaded.current = true;
      loadMetaPixel(META_PIXEL_ID);
    }

    // Also listen for consent being granted later in this session.
    const handleConsentChange = (event: Event) => {
      const detail = (event as CustomEvent<ConsentPreferences>).detail;
      const allowed = detail?.analytics === true && hasConsent("analytics");
      const fbq = (window as Window & { fbq?: FbqFn }).fbq;
      if (!allowed && fbq?.queue) {
        fbq.queue = fbq.queue.filter(
          (command) =>
            !Array.isArray(command) ||
            ![
              "track",
              "trackCustom",
              "trackSingle",
              "trackSingleCustom",
            ].includes(String(command[0]))
        );
      }
      try {
        if (typeof fbq === "function")
          fbq("consent", allowed ? "grant" : "revoke");
      } catch {
        /* Optional vendor failures must not interrupt consent handling. */
      }
      if (allowed && !loaded.current) {
        loaded.current = true;
        loadMetaPixel(META_PIXEL_ID!);
      }
    };

    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChange);
    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChange);
    };
  }, []);

  return null;
}
